import { userContext } from "@frontend/contexts/UserContext";
import { setSentryUser } from "@frontend/instrumentation";
import { getTenantSlug, loadTenantBranding } from "@frontend/tenant/tenant.resolver";
import { setActiveTeamIdForRequests } from "@frontend/utils/active_team_header.client";
import { authClient } from "@frontend/utils/auth.client";
import { saveLastLogin } from "@frontend/utils/auth.persistence";
import { orpcFetch } from "@frontend/utils/orpc.client";
import { consumeFreshSessionRequest } from "@frontend/utils/session_refresh.client";
import { detectUserTimezone } from "@frontend/utils/timezone.utils";
import type { LoaderFunctionArgs } from "react-router";
import { redirect } from "react-router";
import { toast } from "react-toastify";

type Memberships = Awaited<ReturnType<typeof orpcFetch.teams.listMyActiveTeamIds>>;

/**
 * The caller's memberships, read once per sign-in: every protected page
 * load needs them (the tenant gate, and where an exhibitor may go), and
 * they only change when an organiser adds or removes someone.
 */
let membershipsFor: { userId: string; read: Promise<Memberships> } | null = null;
function memberships(userId: string): Promise<Memberships> {
	if (membershipsFor?.userId !== userId) {
		const read = orpcFetch.teams.listMyActiveTeamIds();
		membershipsFor = { userId, read };
		read.catch(() => {
			if (membershipsFor?.read === read) membershipsFor = null;
		});
	}
	return membershipsFor.read;
}

/** What a team's exhibitor may open there (root ADR-036): home lists their stands. */
const EXHIBITOR_PATHS = ["/dashboard", "/stands", "/profile", "/x"];

/**
 * Tenant-host gate. On `<slug>.<root>` the app is locked to ONE team:
 *   1. the user must be a member of it — otherwise `/auth/no-access`;
 *   2. it is forced to be the active team, so every `x-team-id` header
 *      points at the tenant team before any component mounts.
 * Runs inside the loader (not a React effect) so the first paint is already
 * scoped correctly — no flash of another team's data. On the shared app this
 * is a no-op.
 *
 * Returns the (possibly updated) activeTeamAppId.
 */
async function enforceTenantTeam(user: {
	id: string;
	activeTeamAppId?: string | null;
}): Promise<string | null> {
	const current = user.activeTeamAppId ?? null;
	if (!getTenantSlug()) return current;
	const branding = await loadTenantBranding();
	// Unknown slug: TenantGate renders "workspace not found"; nothing to force.
	if (!branding) return current;

	const { teamIds } = await memberships(user.id);
	if (!teamIds.includes(branding.teamId)) {
		throw redirect("/auth/no-access");
	}
	if (current === branding.teamId) return current;

	const { activeTeamAppId } = await orpcFetch.teams.setActiveTeam({
		teamAppId: branding.teamId,
	});
	// `setActiveTeam` writes the user row behind better-auth's back, and the
	// signed session cookie (`session.cookieCache`, five minutes) keeps a
	// copy of the row: every RPC would carry the previous team in the cookie
	// and the tenant team in `x-team-id`, and the gate read that as
	// "Active team id mismatch" until the cookie expired. One uncached read
	// makes better-auth re-sign the cookie from the row, so the next loader
	// run and every RPC after it agree with the header. Best effort: the
	// gate also re-reads the row when the two disagree.
	await authClient.getSession({ query: { disableCookieCache: true } }).catch(() => undefined);
	return activeTeamAppId;
}

/**
 * Auth loader for protected routes. Fetches the session from the backend on
 * every load (online-only — no cached fallback), seeds the `x-team-id`
 * header cache, applies the tenant gate, and redirects to login when there
 * is no session.
 */
export async function authLoader({ context, request }: LoaderFunctionArgs) {
	try {
		// A team switch (or anything else that writes the `users` row outside
		// better-auth) asks for one uncached read here. Without it the 5-minute
		// `session.cookieCache` keeps serving the pre-switch `activeTeamAppId`
		// and the revalidation that follows the switch silently undoes it.
		const disableCookieCache = consumeFreshSessionRequest();
		const { data: session, error } = await authClient.getSession(
			disableCookieCache ? { query: { disableCookieCache: true } } : undefined,
		);

		if (error || !session) {
			const url = new URL(request.url);
			const next = url.pathname === "/" || url.pathname === "/dashboard" ? "" : `?next=${encodeURIComponent(url.pathname + url.search)}`;
			throw redirect(`/auth/login${next}`);
		}


		// Save basic info for login page remembered state
		saveLastLogin({
			name: session.user.name,
			email: session.user.email,
			image: session.user.image,
		});

		// Timezone Detection and Auto-Update
		try {
			const detectedTimezone = await detectUserTimezone();
			if (detectedTimezone && detectedTimezone !== session.user.timezone) {
				await authClient.updateUser({ timezone: detectedTimezone });
				session.user.timezone = detectedTimezone;
				toast.success("Your timezone has been updated to match your current location", {
					position: "top-center",
					autoClose: 3000,
				});
			}
		} catch (timezoneError) {
			console.error(timezoneError);
			toast.error("Timezone detection/update failed.");
		}

		// Seed the `x-team-id` header cache BEFORE any component mounts.
		// The RPC link's headers callback awaits this seed — if it isn't
		// set by the time the first team-scoped query fires, the backend
		// rejects with 403 "Active team id mismatch".
		setActiveTeamIdForRequests(session.user.activeTeamAppId ?? null);

		// Self-heal for stale better-auth cookieCache (`auth.config.ts`
		// `session.cookieCache.maxAge = 5min`). When the cookie was signed
		// before `users.activeTeamAppId` was set on the row, `getSession()`
		// keeps returning `null` for up to 5 minutes. `getDefaultTeam` is
		// the server's canonical fallback: returns the existing personal
		// team, or creates one, and stamps it back onto the user row.
		// Uses `rpcProtectedProcedure` (no `x-team-id` required), so calling
		// it with the header cache seeded to `null` is safe.
		if (!session.user.activeTeamAppId) {
			try {
				const defaultTeam = await orpcFetch.teams.getDefaultTeam();
				session.user.activeTeamAppId = defaultTeam.id;
				setActiveTeamIdForRequests(defaultTeam.id);
			} catch (err) {
				console.warn("[authLoader] getDefaultTeam fallback failed", err);
			}
		}

		// Tenant host → lock to that team (membership check + forced
		// active team). Session-only RPCs, so a null header cache is fine.
		const forced = await enforceTenantTeam(session.user);
		if (forced !== (session.user.activeTeamAppId ?? null)) {
			session.user.activeTeamAppId = forced;
			setActiveTeamIdForRequests(forced);
		}

		// Exhibitors (root ADR-036). A person signed in to a team as its
		// exhibitor sees their stands and nothing of the team: home, the
		// stands, their profile. Anywhere else, home lists their stands too
		// (it reads them on every team), so no team needs a subdomain; and on
		// their own team they may still organise events of their own.
		const mine = await memberships(session.user.id);
		const exhibitsFor = (teamId: string | null | undefined) =>
			mine.exhibitorTeams.some((t) => t.teamId === teamId);
		const path = new URL(request.url).pathname;
		if (
			exhibitsFor(session.user.activeTeamAppId) &&
			!EXHIBITOR_PATHS.some((p) => path === p || path.startsWith(`${p}/`))
		) {
			throw redirect("/dashboard");
		}

		const sessionInfo = {
			hasSession: true,
			user: session.user,
			isRegistered: true, // better-auth handles registration
		};

		setSentryUser({
			email: session.user.email,
			username: session.user.name,
			id: session.user.id,
		});

		// Set user context in React Router context
		context.set(userContext, sessionInfo);

		return sessionInfo;
	} catch (error) {
		if (error instanceof Response) throw error; // Re-throw redirects
		console.error("Auth loader error:", error);
		throw redirect("/auth/login");
	}
}
